// import components
import { Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { Icon } from '@sdb/planning-ui';
import DutyLineOptionEditCell from '../components/DutyLineOptionEditCell';
import { cacheDutyLabel, cacheWaLabel } from '../components/DutyLineOptionEditCell.cache';
import TooltipCell from '../components/TooltipCell';
import TextWithIconCell from '../components/TextWithIconCell';

// import external libraries
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// import types
import { HoursTableOptionType } from './useGetColumns.types';
import { DutyLineGridRow } from '../HoursTable.types';

// import styles
import { StyledBoxFlexCenterSmallGapFullWidth } from 'components/shared/Box/Box.styles';
import {
  getPeriodsStartAndEndTime,
  isRowBlocked,
  isRowCredited,
  isRowCrediting,
  isRowNominal,
  statusApproval
} from '../HoursTable.utils';
import { StyledHighlightedCell } from './useGetColumns.styles';
import IconWithContainer from '../components/IconCell';

export const useGetColumns = ({
  startDate,
  endDate,
  employeeId,
  costCenters
}: {
  startDate: string;
  endDate: string;
  employeeId: number;
  costCenters: HoursTableOptionType[];
}) => {
  const { t } = useTranslation('common');

  const costCenterLabelById = useMemo(
    () =>
      Object.fromEntries(
        (costCenters ?? []).map((option) => [option.value, option.label])
      ),
    [costCenters]
  );

  return useMemo<GridColDef<DutyLineGridRow>[]>(
    () => [
      {
        headerName: t('hoursTable.week'),
        field: 'weekNumber',
        type: 'string',
        width: 60,
        editable: false
      },
      {
        headerName: t('date'),
        field: 'date',
        type: 'string',
        width: 100,
        editable: false,
        renderCell: (params) => {
          return (
            <>
              {(isRowCrediting(params.row) ||
                isRowCredited(params.row) ||
                isRowNominal(params.row)) && (
                <StyledHighlightedCell
                  style="corner"
                  color={
                    isRowCrediting(params.row)
                      ? 'errorBg'
                      : isRowCredited(params.row)
                      ? 'infoBg'
                      : isRowNominal(params.row)
                      ? 'warningBg'
                      : undefined
                  }
                />
              )}
              {params.value}
            </>
          );
        }
      },
      {
        headerName: t('wa'),
        field: 'dvb',
        type: 'singleSelect',
        width: 80,
        editable: true,
        valueFormatter: (value) =>
          value != null ? cacheWaLabel[value] ?? value : '',
        renderEditCell: (params) => (
          <DutyLineOptionEditCell
            {...params}
            fetchKind="workAgreement"
            startDate={startDate}
            endDate={endDate}
            employeeId={employeeId}
          />
        )
      },
      {
        headerName: t('hoursTable.request'),
        field: 'requestId',
        type: 'string',
        width: 80,
        editable: false
      },
      {
        headerName: t('organizationalUnit'),
        field: 'organizationalUnit',
        editable: true,
        type: 'singleSelect',
        flex: 1,
        valueFormatter: (value) =>
          value != null && costCenterLabelById
            ? costCenterLabelById[value]
            : '',
        renderEditCell: (params) => (
          <DutyLineOptionEditCell {...params} options={costCenters ?? []} />
        )
      },
      {
        headerName: t('hoursTable.dutyCode'),
        field: 'displayDutyCode',
        type: 'singleSelect',
        width: 100,
        editable: true,
        valueFormatter: (value) =>
          value != null ? cacheDutyLabel[value] ?? value : '',
        renderEditCell: (params) => (
          <DutyLineOptionEditCell
            {...params}
            fetchKind="basicDuty"
            startDate={startDate}
            endDate={endDate}
            employeeId={employeeId}
          />
        )
      },
      {
        headerName: t('period'),
        field: 'periods',
        type: 'string',
        width: 100,
        editable: true,
        valueFormatter: (_, rows) => getPeriodsStartAndEndTime(rows ?? []),
        renderCell: (params) => {
          if (isRowNominal(params.row)) return '';
          const periods = params.row.periods ?? [];
          return (
            <TooltipCell value={params.formattedValue} periods={periods} />
          );
        },
        renderEditCell: (params) => (
          <TextWithIconCell value={params.formattedValue} />
        )
      },
      {
        headerName: t('hoursTable.hours'),
        field: 'dutyTime',
        type: 'string',
        width: 80,
        editable: true
      },
      {
        headerName: t('generatedReport.duty_type'),
        field: 'dutyType',
        type: 'string',
        width: 80,
        editable: true,
        valueGetter: (params, row) => {
          if (isRowNominal(row)) return row.nominalDutyTypeCode;
          return params;
        },
        valueFormatter: (value) => (value != null ? String(value) : ''),
        renderCell: (params) => {
          const periods = params.row.periods ?? [];
          if (isRowNominal(params.row)) {
            return (
              <TooltipCell
                value={params.value}
                periods={periods}
                customTooltipText={`${params.value} - ${params.row.nominalDutyTypeDescription}`}
              />
            );
          }
          return <TooltipCell value={params.value} periods={periods} />;
        },
        renderEditCell: (params) =>
          isRowNominal(params.row) ? (
            <DutyLineOptionEditCell
              {...params}
              fetchKind="dutyHourTypes"
              startDate={startDate}
              endDate={endDate}
            />
          ) : (
            <TextWithIconCell value={params.formattedValue} />
          )
      },
      {
        headerName: t('hoursTable.credit'),
        field: 'credit',
        type: 'string',
        width: 60,
        editable: false,
        valueGetter: (_, row) => {
          if (row.creditDutyLineId) return t('hoursTable.credited');
          if (isRowCrediting(row)) return t('hoursTable.credit');
          return;
        }
      },
      {
        headerName: t('hoursTable.statusApproval'),
        field: 'statusApproval',
        type: 'string',
        width: 90,
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const statusItem = statusApproval(params.row);

          if (statusItem) {
            return (
              <IconWithContainer
                iconColor={statusItem.color}
                icon={statusItem.icon}
              />
            );
          }

          return params.value;
        }
      },
      {
        headerName: t('status'),
        field: 'status',
        type: 'string',
        width: 60,
        editable: false,
        valueGetter: (_, row) => {
          if (isRowBlocked(row)) return t('hoursTable.blocked');

          return row.status;
        },
        renderCell: (params) => {
          return <TooltipCell value={params.value} />;
        }
      },
      {
        headerName: t('hoursTable.error'),
        field: 'error',
        type: 'string',
        width: 60,
        editable: false,
        renderCell: (params) => {
          if (!params.row.error) return '';
          return (
            <Tooltip title={params.row.error} arrow>
              <StyledBoxFlexCenterSmallGapFullWidth>
                <Icon type="triangle-exclamation" color="warning" />
                {params.row.error}
              </StyledBoxFlexCenterSmallGapFullWidth>
            </Tooltip>
          );
        }
      }
    ],
    [t, startDate, endDate, employeeId, costCenters]
  );
};
